import { yupResolver } from '@hookform/resolvers/yup'
import { map } from 'lodash-es'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import * as yup from 'yup'

import { EditUserCustomPermissions } from './EditUserCustomPermissions'
import Loader from '../../../../common/components/Loader'
import { UserGroups } from '../../../../common/models'
import { User } from '../../../../common/models/user'
import FormGroup from '../../../../core/components/FormGroup'
import { formatDateTime } from '../../../../core/constants/times'
import { useCheckPermission } from '../../../../core/hooks/use-check-permission'
import {
  getAllUserGroups,
  getUserGroup,
} from '../../../../core/services/permissions'
import {
  TCustomPermissions,
  getUser,
  updateUser,
} from '../../../../core/services/users'
import { MessageBox } from '../../messages/MessageBox'
import { SaveConfirmationModal } from '../../SaveConfirmationModal'
import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox, Input, Label } from '@/components/ui'
import { XCircle } from 'lucide-react'
import { Save } from 'lucide-react'
import SelectField from '@/components/select-field'

type CustomPermissions = {
  id?: string | null
  name: string
  permissions: { id: string; view: boolean; edit: boolean; name: string }[]
}

export interface IFormValues {
  name: string
  phone: string
  email: string
  password?: string
  userGroup: string
  active: boolean
  customPermission: boolean
  customPermissions?: CustomPermissions[]
}

export const SettingPermissionsViewUserPage: React.FC = () => {
  const { hasEditPermission } = useCheckPermission({
    page: 'PERMISSIONS_EDIT_USER',
  })
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [userGroups, setUserGroups] = useState<UserGroups[]>([])
  const [user, setUser] = useState<User | null>(null)
  const { festivalName, editionId, userId } = useParams()
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [showSaveConfirm, setShowSaveConfirm] = useState<boolean>(false)

  const schema = yup.object().shape({
    name: yup
      .string()
      .required('Name is required')
      .test('name', 'Name is required', (value) => {
        if (value) {
          return value.split(' ').length === 2
        }
        return false
      }),
    phone: yup.string().required('Phone is required'),
    email: yup.string().required('Email is required'),
    password: yup.string().optional(),
    userGroup: yup.string().required('User Group is required'),
    active: yup.boolean().oneOf([true, false]).required('Active is required'),
    customPermission: yup
      .boolean()
      .oneOf([true, false])
      .required('Active is required'),
    customPermissions: yup
      .array()
      .optional()
      .when('customPermission', {
        is: (val: boolean) => val,
        then: (schema) =>
          schema.of(
            yup.object({
              id: yup.string().required(),
              name: yup.string().optional(),
              permissions: yup.array().of(
                yup.object({
                  id: yup.string().required(),
                  view: yup.boolean().required(),
                  edit: yup.boolean().required(),
                  name: yup.string().required(),
                }),
              ),
            }),
          ),
        otherwise: (schema) => schema.optional(),
      }),
  })

  const getData = useCallback(async () => {
    if (!userId) {
      return
    }
    try {
      setLoading(true)
      const { data: userGroupsData, status: userGroupStatus } =
        await getAllUserGroups({
          size: 100,
        })

      if (userGroupStatus === 200) {
        setUserGroups(userGroupsData.content)
      }
      const { data, status } = await getUser(userId)
      if (status === 200) {
        setValue('name', data.firstName + ' ' + data.lastName)
        setValue('email', data.email)
        setValue('phone', data.phone)
        setValue(
          'userGroup',
          userGroupsData.content.find((one) => one.id === data.userGroup?.id)
            ?.id || '',
        )
        if (data.userGroup?.id) {
          const { data: userGroupData } = await getUserGroup(data.userGroup?.id)

          setValue('active', data.active)
          setValue('customPermission', data.customPermission)
          setUser(data)
          if (data.userGroup) {
            setValue(
              'customPermissions',
              userGroupData.permissionCategories.map((one) => {
                return {
                  id: one.id,
                  name: one.name,
                  permissions: one.permissions.map((permission) => {
                    const prevData = data.customPermissions.find(
                      (one) => one.permissionId === permission.id,
                    )
                    return {
                      id: permission.id as string,
                      view: prevData?.view ?? permission.view,
                      edit: prevData?.edit ?? permission.edit,
                      name: permission.name,
                    }
                  }),
                }
              }),
            )
          }
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    getData()
  }, [userId, getData])

  const {
    handleSubmit,
    register,
    watch,
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm<IFormValues>({
    mode: 'onChange',
    resolver: yupResolver<IFormValues>(schema),
  })

  const customPermission = watch('customPermission')

  const { fields } = useFieldArray({
    control,
    name: 'customPermissions',
  })

  const submit = async (values: IFormValues) => {
    if (!userId) {
      return
    }

    const customPermissions: TCustomPermissions[] = []

    fields.forEach((field) => {
      field.permissions.forEach((permission) => {
        customPermissions.push({
          permissionId: permission.id,
          view: permission.view,
          edit: permission.edit,
        })
      })
    })
    try {
      setSubmitting(true)
      const { status } = await updateUser(userId, {
        email: values.email,
        firstName: values.name.split(' ')[0],
        lastName: values.name.split(' ')[1],
        phone: values.phone,
        active: values.active,
        customPermission: values.customPermission,
        userGroupId: values.userGroup,
        password: values.password || null,
        customPermissions: customPermissions,
      })
      if (status === 200) {
        navigate(`/settings/permissions/users/${festivalName}/${editionId}`)
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setIsError(true)
      setShowMessageBox(true)
      setMessage(e?.response?.data?.developerMessage)
    } finally {
      setSubmitting(false)
    }
  }

  const getUserGroupValues = async (userGroupId: string) => {
    try {
      setLoading(true)
      const { data: userGroupData } = await getUserGroup(userGroupId)
      setValue(
        'customPermissions',
        userGroupData.permissionCategories.map((one) => {
          return {
            id: one.id,
            name: one.name,
            permissions: one.permissions.map((permission) => {
              const prevData = user?.customPermissions.find(
                (one) => one.permissionId === permission.id,
              )
              return {
                id: permission.id as string,
                view: prevData?.view ?? permission.view,
                edit: prevData?.edit ?? permission.edit,
                name: permission.name,
              }
            }),
          }
        }),
      )
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = async () => {
    navigate(`/settings/permissions/users/${festivalName}/${editionId}`)
  }

  return (
    <>
      <Header pageTitle="View User" />
      {(submitting || loading) && <Loader />}
      <Card className="p-5 backdrop-blur-lg max-h-[calc(100vh-150px)] overflow-y-auto">
        <div className="flex items-center justify-between gap-4 mb-6">
          <PageTitle title="View User" icon="users-filled" />
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={handleCancel}>
              <XCircle className="w-4 h-4" />
              Cancel
            </Button>
            <Button
              type="submit"
              form="settings-view-user-form"
              variant="primary"
              disabled={submitting || !hasEditPermission}
              loading={submitting}
            >
              <Save className="w-4 h-4" />
              Save
            </Button>
          </div>
        </div>
        <div className="flex flex-col items-start gap-4 w-full">
          <form
            id="settings-view-user-form"
            className="w-full space-y-7"
            onSubmit={handleSubmit(() => setShowSaveConfirm(true))}
          >
            <FormGroup label="Name">
              <Input
                {...register('name')}
                type="text"
                isInvalid={!!errors.name?.message}
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <FormGroup label="Phone">
              <Input
                {...register('phone')}
                type="text"
                isInvalid={!!errors.phone?.message}
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <FormGroup label="Email">
              <Input
                {...register('email')}
                type="text"
                isInvalid={!!errors.email?.message}
                autoComplete="off"
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <FormGroup label="Password">
              <Input
                {...register('password')}
                type="password"
                isInvalid={!!errors.password?.message}
                autoComplete="new-password"
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <Controller
              name="userGroup"
              control={control}
              render={({ field }) => (
                <FormGroup label="User group">
                  <SelectField
                    outline
                    error={errors?.userGroup?.message}
                    value={field.value}
                    onValueChange={field.onChange}
                    options={map(userGroups, (group) => ({
                      value: group.id,
                      label: group.name,
                    }))}
                    placeholder="Select Group"
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
              )}
            />
            <div>
              <Label className="text-body-medium text-monochrome-1">
                Last Activity
              </Label>
              {user?.lastActivityDate ? (
                <span className="text-body-medium text-monochrome-1">
                  {formatDateTime(user?.lastActivityDate)}
                </span>
              ) : (
                <span className="text-body-medium text-monochrome-1">
                  -----
                </span>
              )}
            </div>
            <FormGroup
              label="Active"
              direction="row"
              labelClassName="whitespace-nowrap"
            >
              <Checkbox
                checked={watch('active')}
                onCheckedChange={(checked) => {
                  setValue('active', Boolean(checked as boolean))
                }}
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <FormGroup
              label="Custom Permission"
              direction="row"
              labelClassName="whitespace-nowrap"
            >
              <Checkbox
                checked={watch('customPermission')}
                onCheckedChange={(checked) => {
                  setValue('customPermission', Boolean(checked as boolean))
                }}
                disabled={!hasEditPermission}
              />
            </FormGroup>
            {customPermission && (
              <>
                {map(fields, (field, index) => (
                  <div key={field.id} className="mb-2">
                    <Label className="text-body-medium text-monochrome-1">
                      {field.name}
                    </Label>
                    <EditUserCustomPermissions
                      index={index}
                      control={control}
                      setValue={setValue}
                      errors={errors}
                      watch={watch}
                      register={register}
                      disabled={!hasEditPermission}
                    />
                  </div>
                ))}
              </>
            )}
          </form>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
      <SaveConfirmationModal
        open={showSaveConfirm}
        onOpenChange={setShowSaveConfirm}
        onConfirm={handleSubmit(submit)}
      />
    </>
  )
}
